import type { Edge, Node } from '../../../core/models/scene';

export type OrgReportingValidationCode =
  | 'missing_endpoint'
  | 'self_reporting'
  | 'multiple_managers'
  | 'reporting_cycle';

export interface OrgReportingValidationResult {
  valid: boolean;
  code?: OrgReportingValidationCode;
  message?: string;
}

const isPrimaryReportingEdge = (edge: Edge): boolean => {
  const type = String(edge.type ?? '');
  const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
  const relation = String(attrs.relation ?? '');
  const dotted = attrs.dotted === true || type === 'dotted' || relation === 'dotted';
  return !dotted && (
    type === 'reporting' ||
    type === 'reports_to' ||
    type === 'scenario_reporting' ||
    relation === 'reporting' ||
    relation === 'reports_to'
  );
};

export function validateOrgReportingRelationship(input: {
  fromId: string;
  toId: string;
  dotted?: boolean;
  nodes: readonly Node[];
  edges: readonly Edge[];
}): OrgReportingValidationResult {
  const { fromId, toId, dotted = false, nodes, edges } = input;

  if (!nodes.some((node) => node.id === fromId) || !nodes.some((node) => node.id === toId)) {
    return { valid: false, code: 'missing_endpoint', message: 'Both reporting positions must exist.' };
  }
  if (fromId === toId) {
    return { valid: false, code: 'self_reporting', message: 'A position cannot report to itself.' };
  }

  // Secondary/dotted relationships intentionally allow more than one manager
  // and do not define the primary hierarchy used for cycle detection.
  if (dotted) return { valid: true };

  const primaryEdges = edges.filter(isPrimaryReportingEdge);
  if (primaryEdges.some((edge) => edge.from === fromId)) {
    return {
      valid: false,
      code: 'multiple_managers',
      message: 'A position can have only one primary manager.',
    };
  }

  const managerByPosition = new Map(primaryEdges.map((edge) => [edge.from, edge.to]));
  const visited = new Set<string>();
  let current: string | undefined = toId;
  while (current) {
    if (current === fromId) {
      return {
        valid: false,
        code: 'reporting_cycle',
        message: 'This reporting relationship would create a cycle.',
      };
    }
    if (visited.has(current)) break;
    visited.add(current);
    current = managerByPosition.get(current);
  }

  return { valid: true };
}
